// NOSONAR-S6747: This file uses Emotion's css prop for CSS-in-JS styling.
// The `css` prop is a legitimate Emotion feature provided by @emotion/core.
// SonarQube's S6747 rule flags it as unknown property, but this is a false positive
// as Emotion transforms these props at build-time via Babel plugin.
// See: https://emotion.sh/docs/css-prop
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { format, parseISO } from 'date-fns'
import Modal from 'react-modal'
import { useHistory } from 'react-router-dom'
import { Text, FormButton, Form } from 'shared/components'
import { InWinImage } from 'routes/shared/components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { event } from 'routes/route.map'
import config from 'config'

import * as styles from './styles'

const getTeamLogo = (team) => {
  if (team?.logo) {
    return team.logo.startsWith('http')
      ? team.logo
      : `${config.assets.baseUrl}${team.logo}`
  }
  return null
}

const getTeamAbbreviation = (teamName, teamObj) => {
  if (teamObj?.teamName) {
    const words = teamObj.teamName.split(' ')
    if (words.length >= 2) {
      return (
        words
          .slice(0, 2)
          .map((word) => word.charAt(0))
          .join('') +
        (words[0].length > 1 ? words[0].charAt(1).toUpperCase() : '')
      )
    }
    return teamObj.teamName.substring(0, 3).toUpperCase()
  }

  if (teamName) {
    return teamName.substring(0, 3).toUpperCase()
  }

  return 'TBD'
}

const getTeamColor = (teamAbbr) => {
  const teamColors = {
    CHI: '#DC143C',
    CLE: '#1D1160',
    GS: '#FFC72C',
    IND: '#002D62',
    DET: '#C8102E',
    BKN: '#000000',
    LAL: '#FDB927', // Lakers gold
  }
  return teamColors[teamAbbr] || '#6B73FF'
}

const formatEventDateTime = (startTime) => {
  if (!startTime) return { date: '', time: '', timezone: '' }

  try {
    const eventTime = parseISO(startTime)
    return {
      date: format(eventTime, 'MMM dd, yyyy').toUpperCase(),
      time: format(eventTime, 'hh:mm a'),
      timezone: 'CEST, UTC+2', // This could be dynamic based on user timezone
    }
  } catch {
    return { date: '', time: '', timezone: '' }
  }
}

const handleKeyboardActivation = (handler) => (event) => {
  if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
    event.preventDefault()
    handler(event)
  }
}

const MobileInWinModalComponent = ({
  game,
  isOpen,
  onClose,
  form,
  onSubmit,
  currentBalance,
  loadBeneficiaries,
  loadTriggers,
  loadSponsors,
}) => {
  const history = useHistory()
  const [totalAmount, setTotalAmount] = useState(0)
  const [sponsorLimit, setSponsorLimit] = useState(0)
  const [loadedBeneficiaries, setLoadedBeneficiaries] = useState([])
  const [loadedTriggers, setLoadedTriggers] = useState([])
  const [loadedSponsors, setLoadedSponsors] = useState([])
  const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState('')
  const [selectedTriggerId, setSelectedTriggerId] = useState('')
  const [selectedSponsorId, setSelectedSponsorId] = useState('')
  const [sponsorAmountValue, setSponsorAmountValue] = useState('')
  const [mainAmountValue, setMainAmountValue] = useState('')
  const [isBeneficiaryDropdownOpen, setIsBeneficiaryDropdownOpen] =
    useState(false)
  const [isSponsorDropdownOpen, setIsSponsorDropdownOpen] = useState(false)
  const [isTriggerDropdownOpen, setIsTriggerDropdownOpen] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showTopError, setShowTopError] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showTopSuccess, setShowTopSuccess] = useState(false)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isBeneficiaryDropdownOpen &&
        !event.target.closest('[data-dropdown="beneficiary"]')
      ) {
        setIsBeneficiaryDropdownOpen(false)
      }
      if (
        isSponsorDropdownOpen &&
        !event.target.closest('[data-dropdown="sponsor"]')
      ) {
        setIsSponsorDropdownOpen(false)
      }
      if (
        isTriggerDropdownOpen &&
        !event.target.closest('[data-dropdown="trigger"]')
      ) {
        setIsTriggerDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isBeneficiaryDropdownOpen, isSponsorDropdownOpen, isTriggerDropdownOpen])

  const teamAAbbr = getTeamAbbreviation(game.teamA, game.teamOne)
  const teamBAbbr = getTeamAbbreviation(game.teamB, game.teamTwo)
  const teamAColor = getTeamColor(teamAAbbr)
  const teamBColor = getTeamColor(teamBAbbr)
  const { date, time, timezone } = formatEventDateTime(game.startTime)

  // Load initial data when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset local state
      setSelectedBeneficiaryId('')
      setSelectedTriggerId('')
      setSelectedSponsorId('')
      setSponsorAmountValue('')
      setMainAmountValue('')
      setIsBeneficiaryDropdownOpen(false)
      setIsSponsorDropdownOpen(false)
      setShowConfirmation(false)
      setShowError(false)
      setErrorMessage('')
      setShowTopError(false)
      setShowSuccess(false)
      setShowTopSuccess(false)

      // Load beneficiaries
      loadBeneficiaries('')
        .then((beneficiaries) => {
          setLoadedBeneficiaries(beneficiaries)
        })
        .catch(() => {
          setLoadedBeneficiaries([])
        })

      // Load triggers
      loadTriggers('')
        .then((triggers) => {
          setLoadedTriggers(triggers)
        })
        .catch(() => {
          setLoadedTriggers([])
        })

      // Load sponsors
      loadSponsors('')
        .then((sponsors) => {
          setLoadedSponsors(sponsors)
        })
        .catch(() => {
          setLoadedSponsors([])
        })
    }
  }, [isOpen, loadBeneficiaries, loadTriggers, loadSponsors])

  // Calculate total amount when form values change
  useEffect(() => {
    const userAmount = Number.parseFloat(mainAmountValue || 0)
    const sponsorAmount = Number.parseFloat(sponsorAmountValue || 0)
    setTotalAmount(userAmount + sponsorAmount)
  }, [mainAmountValue, sponsorAmountValue])

  const handleFormSubmit = () => {
    // Show confirmation modal instead of immediately submitting
    setShowConfirmation(true)
  }

  const handleFinalSubmit = async () => {
    try {
      // Close confirmation modal
      setShowConfirmation(false)

      // Construct form data from form fields
      const formData = {
        beneficiary: form?.fields?.beneficiary?.value,
        triggers: form?.fields?.triggers?.value,
        amount: form?.fields?.amount?.value,
        sponsors: form?.fields?.sponsors?.value,
        sponsorAmount: form?.fields?.sponsorAmount?.value,
      }

      await onSubmit(formData)

      // Store success notification in localStorage for events page
      localStorage.setItem(
        'inwinNotification',
        JSON.stringify({
          type: 'success',
          title: 'InWin Successfully!',
          message: 'You have successfully placed your InWin',
          timestamp: Date.now(),
        })
      )

      // Redirect to events page using React Router
      history.push(event.index())
    } catch (error) {
      // Store error notification in localStorage for events page
      localStorage.setItem(
        'inwinNotification',
        JSON.stringify({
          type: 'error',
          title: 'Error!',
          message: 'InWin failed. Check your connection and try again',
          timestamp: Date.now(),
        })
      )

      // Redirect to events page using React Router
      history.push(event.index())
    }
  }

  const handleSponsorChange = (selectedSponsor) => {
    if (form?.fields?.sponsors?.onChange) {
      form.fields.sponsors.onChange({ target: { value: selectedSponsor } })
    }

    // Set sponsor limit and reset sponsor amount if no sponsor selected
    if (selectedSponsor) {
      setSponsorLimit(selectedSponsor.limit || 0)
    } else {
      setSponsorLimit(0)
      if (form?.fields?.sponsorAmount?.onChange) {
        form.fields.sponsorAmount.onChange({ target: { value: 0 } })
      }
    }
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={styles.modalStyles}>
      <div css={styles.modalContainer}>
        {/* Header */}
        <div css={styles.header}>
          <button
            css={[styles.backButton, styles.accessibleButton]}
            onClick={onClose}
            type="button"
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon="arrow-left" />
          </button>
          <Text size={14} css={styles.placeholderText}>
            Select beneficiary
          </Text>

          {/* Event Details */}
          <div css={styles.eventSection}>
            <div css={styles.teamsContainer}>
              {/* Team A */}
              <div css={styles.teamContainer}>
                <div css={styles.teamLogo}>
                  {getTeamLogo(game.teamOne) ? (
                    <InWinImage
                      source={getTeamLogo(game.teamOne)}
                      alt={game.teamA}
                      css={styles.logoImage}
                    />
                  ) : (
                    <>
                      <div
                        css={[
                          styles.logoPlaceholder,
                          { backgroundColor: teamAColor },
                        ]}
                      >
                        <Text size={12} bold color="#ffffff">
                          {teamAAbbr}
                        </Text>
                      </div>
                      <Text size={12} bold css={styles.teamName}>
                        {teamAAbbr}
                      </Text>
                    </>
                  )}
                </div>
              </div>

              {/* Team B */}
              <div css={styles.teamContainer}>
                <div css={styles.teamLogo}>
                  {getTeamLogo(game.teamTwo) ? (
                    <InWinImage
                      source={getTeamLogo(game.teamTwo)}
                      alt={game.teamB}
                      css={styles.logoImage}
                    />
                  ) : (
                    <>
                      <div
                        css={[
                          styles.logoPlaceholder,
                          { backgroundColor: teamBColor },
                        ]}
                      >
                        <Text size={12} bold color="#ffffff">
                          {teamBAbbr}
                        </Text>
                      </div>
                      <Text size={12} bold css={styles.teamName}>
                        {teamBAbbr}
                      </Text>
                    </>
                  )}
                </div>
              </div>

              {/* Vertical Separator between Team B and Date */}
              <div css={styles.verticalSeparator}></div>

              {/* Date and Time */}
              <div css={styles.dateTimeContainer}>
                <Text size={10} css={styles.timezone}>
                  {timezone}
                </Text>
                <Text size={12} bold css={styles.date}>
                  {date}
                </Text>
                <Text size={12} bold css={styles.time}>
                  {time}
                </Text>
              </div>
            </div>
          </div>

          {/* Current Balance */}
          <div css={styles.balanceSection}>
            <div css={styles.balanceContainer}>
              <div css={styles.balanceLabel}>
                <svg
                  width="20"
                  height="16"
                  viewBox="0 0 20 16"
                  fill="none"
                  css={styles.cardIcon}
                >
                  {/* Main card body */}
                  <rect
                    x="0"
                    y="0"
                    width="20"
                    height="16"
                    rx="3"
                    fill="currentColor"
                  />

                  {/* Magnetic stripe */}
                  <rect x="0" y="4" width="20" height="3" fill="#1a1a1a" />

                  {/* Card chip */}
                  <rect
                    x="2"
                    y="9"
                    width="3"
                    height="2.5"
                    rx="0.5"
                    fill="#1a1a1a"
                  />

                  {/* Card number lines */}
                  <rect
                    x="7"
                    y="9.5"
                    width="6"
                    height="0.8"
                    rx="0.4"
                    fill="#1a1a1a"
                  />
                  <rect
                    x="7"
                    y="11"
                    width="4"
                    height="0.8"
                    rx="0.4"
                    fill="#1a1a1a"
                  />

                  {/* Card logo/text area */}
                  <rect
                    x="15"
                    y="9.5"
                    width="3"
                    height="0.8"
                    rx="0.4"
                    fill="#1a1a1a"
                  />
                  <rect
                    x="15"
                    y="11"
                    width="2"
                    height="0.8"
                    rx="0.4"
                    fill="#1a1a1a"
                  />
                </svg>
                <Text size={14}>Current Balance</Text>
              </div>
              <Text size={14} bold css={styles.balanceAmount}>
                $ {currentBalance}
              </Text>
            </div>
          </div>

          {/* Dashed Line Separator */}
          <div css={styles.dashedSeparator}></div>

          {/* Form */}
          {form && (
            <Form
              form={form}
              onSubmit={handleFormSubmit}
              css={styles.formContainer}
            >
              {/* Choose Beneficiary */}
              <div css={styles.fieldSection}>
                <Text size={12} bold css={styles.fieldLabel}>
                  Choose a beneficiary
                </Text>
                <div css={styles.simpleDropdown}>
                  <div
                    css={styles.customDropdownContainer}
                    data-dropdown="beneficiary"
                  >
                    <button
                      type="button"
                      css={styles.customDropdownTrigger}
                      onClick={() =>
                        setIsBeneficiaryDropdownOpen(!isBeneficiaryDropdownOpen)
                      }
                      aria-haspopup="listbox"
                      aria-expanded={isBeneficiaryDropdownOpen}
                      onKeyDown={handleKeyboardActivation((event) =>
                        event.currentTarget.click()
                      )}
                    >
                      {selectedBeneficiaryId ? (
                        <div css={styles.selectedBeneficiaryDisplay}>
                          {(() => {
                            const selectedBeneficiary =
                              loadedBeneficiaries.find(
                                (b) => b.id.toString() === selectedBeneficiaryId
                              )
                            return selectedBeneficiary ? (
                              <>
                                {selectedBeneficiary.logo && (
                                  <InWinImage
                                    source={selectedBeneficiary.logo}
                                    alt={selectedBeneficiary.beneficiaryName}
                                    css={styles.beneficiaryLogo}
                                  />
                                )}
                                <Text size={14} css={styles.beneficiaryName}>
                                  {selectedBeneficiary.beneficiaryName}
                                </Text>
                              </>
                            ) : (
                              <Text size={14} css={styles.placeholderText}>
                                Select beneficiary
                              </Text>
                            )
                          })()}
                        </div>
                      ) : (
                        <Text size={14} css={styles.placeholderText}>
                          Select beneficiary
                        </Text>
                      )}
                      <div css={styles.dropdownArrow}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 16 16"
                          css={styles.arrowIcon}
                        >
                          <path
                            fill="none"
                            stroke="#999"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m4 6 4 4 4-4"
                          />
                        </svg>
                      </div>
                    </button>
                  </div>

                  {isBeneficiaryDropdownOpen && (
                    <ul css={styles.customDropdownOptions}>
                      <li>
                        <button
                          type="button"
                          css={styles.customDropdownOption}
                          onClick={() => {
                            setSelectedBeneficiaryId('')
                            setIsBeneficiaryDropdownOpen(false)
                            if (form?.fields?.beneficiary?.onChange) {
                              form.fields.beneficiary.onChange({
                                target: { value: null },
                              })
                            }
                          }}
                          onKeyDown={handleKeyboardActivation((event) =>
                            event.currentTarget.click()
                          )}
                        >
                          <Text size={14} css={styles.placeholderText}>
                            Select beneficiary
                          </Text>
                        </button>
                      </li>
                      {loadedBeneficiaries.map((beneficiary) => (
                        <li key={beneficiary.id}>
                          <button
                            type="button"
                            css={styles.customDropdownOption}
                            onClick={() => {
                              setSelectedBeneficiaryId(
                                beneficiary.id.toString()
                              )
                              setIsBeneficiaryDropdownOpen(false)
                              if (form?.fields?.beneficiary?.onChange) {
                                setTimeout(() => {
                                  form.fields.beneficiary.onChange({
                                    target: { value: beneficiary },
                                  })
                                }, 0)
                              }
                            }}
                            onKeyDown={handleKeyboardActivation((event) =>
                              event.currentTarget.click()
                            )}
                          >
                            {beneficiary.logo && (
                              <InWinImage
                                source={beneficiary.logo}
                                alt={beneficiary.beneficiaryName}
                                css={styles.beneficiaryLogo}
                              />
                            )}
                            <Text size={14} css={styles.beneficiaryName}>
                              {beneficiary.beneficiaryName}
                            </Text>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Dashed Line Separator */}
              <div css={styles.dashedSeparatorInForm}></div>

              {/* Choose Triggers */}
              <div css={styles.fieldSection}>
                <Text size={12} bold css={styles.fieldLabel}>
                  Choose triggers
                </Text>
                <div css={styles.simpleDropdown}>
                  <div
                    css={styles.customDropdownContainer}
                    data-dropdown="trigger"
                  >
                    <button
                      type="button"
                      css={styles.customDropdownTrigger}
                      onClick={() =>
                        setIsTriggerDropdownOpen(!isTriggerDropdownOpen)
                      }
                      aria-haspopup="listbox"
                      aria-expanded={isTriggerDropdownOpen}
                      onKeyDown={handleKeyboardActivation((event) =>
                        event.currentTarget.click()
                      )}
                    >
                      {selectedTriggerId ? (
                        <div css={styles.selectedBeneficiaryDisplay}>
                          {(() => {
                            const selectedTrigger = loadedTriggers.find(
                              (t) => t.id.toString() === selectedTriggerId
                            )
                            return selectedTrigger ? (
                              <>
                                {selectedTrigger.logo && (
                                  <InWinImage
                                    source={selectedTrigger.logo}
                                    alt={selectedTrigger.description}
                                    css={styles.beneficiaryLogo}
                                  />
                                )}
                                <Text size={14} css={styles.beneficiaryName}>
                                  {selectedTrigger.description}
                                </Text>
                              </>
                            ) : (
                              <Text size={14} css={styles.placeholderText}>
                                Select trigger
                              </Text>
                            )
                          })()}
                        </div>
                      ) : (
                        <Text size={14} css={styles.placeholderText}>
                          Select trigger
                        </Text>
                      )}
                      <div css={styles.dropdownArrow}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 16 16"
                          css={styles.arrowIcon}
                        >
                          <path
                            fill="none"
                            stroke="#999"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m4 6 4 4 4-4"
                          />
                        </svg>
                      </div>
                    </button>

                    {isTriggerDropdownOpen && (
                      <ul css={styles.customDropdownOptions}>
                        <li>
                          <button
                            type="button"
                            css={styles.customDropdownOption}
                            onClick={() => {
                              setSelectedTriggerId('')
                              setIsTriggerDropdownOpen(false)
                              if (form?.fields?.triggers?.onChange) {
                                form.fields.triggers.onChange({
                                  target: { value: null },
                                })
                              }
                            }}
                            onKeyDown={handleKeyboardActivation((event) =>
                              event.currentTarget.click()
                            )}
                          >
                            <Text size={14} css={styles.placeholderText}>
                              Select trigger
                            </Text>
                          </button>
                        </li>
                        {loadedTriggers.map((trigger) => (
                          <li key={trigger.id}>
                            <button
                              type="button"
                              css={styles.customDropdownOption}
                              onClick={() => {
                                setSelectedTriggerId(trigger.id.toString())
                                setIsTriggerDropdownOpen(false)
                                if (form?.fields?.triggers?.onChange) {
                                  setTimeout(() => {
                                    form.fields.triggers.onChange({
                                      target: { value: trigger },
                                    })
                                  }, 0)
                                }
                              }}
                              onKeyDown={handleKeyboardActivation((event) =>
                                event.currentTarget.click()
                              )}
                            >
                              {trigger.logo && (
                                <InWinImage
                                  source={trigger.logo}
                                  alt={trigger.description}
                                  css={styles.beneficiaryLogo}
                                />
                              )}
                              <Text size={14} css={styles.beneficiaryName}>
                                {trigger.description}
                              </Text>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {/* Dashed Line Separator */}
              <div css={styles.dashedSeparatorInForm}></div>

              {/* Choose Sponsor */}
              <div css={styles.fieldSection}>
                <Text size={12} bold css={styles.fieldLabel}>
                  Choose a sponsor
                </Text>
                <div css={styles.simpleDropdown}>
                  <div
                    css={styles.customDropdownContainer}
                    data-dropdown="sponsor"
                  >
                    <button
                      type="button"
                      css={styles.customDropdownTrigger}
                      onClick={() =>
                        setIsSponsorDropdownOpen(!isSponsorDropdownOpen)
                      }
                      aria-haspopup="listbox"
                      aria-expanded={isSponsorDropdownOpen}
                      onKeyDown={handleKeyboardActivation((event) =>
                        event.currentTarget.click()
                      )}
                    >
                      {selectedSponsorId ? (
                        <div css={styles.selectedBeneficiaryDisplay}>
                          {(() => {
                            const selectedSponsor = loadedSponsors.find(
                              (s) => s.id.toString() === selectedSponsorId
                            )
                            return selectedSponsor ? (
                              <>
                                {selectedSponsor.logo && (
                                  <InWinImage
                                    source={selectedSponsor.logo}
                                    alt={selectedSponsor.fullName}
                                    css={styles.beneficiaryLogo}
                                  />
                                )}
                                <Text size={14} css={styles.beneficiaryName}>
                                  {selectedSponsor.fullName}
                                </Text>
                              </>
                            ) : (
                              <Text size={14} css={styles.placeholderText}>
                                Select sponsor
                              </Text>
                            )
                          })()}
                        </div>
                      ) : (
                        <Text size={14} css={styles.placeholderText}>
                          Select sponsor
                        </Text>
                      )}
                      <div css={styles.dropdownArrow}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 16 16"
                          css={styles.arrowIcon}
                        >
                          <path
                            fill="none"
                            stroke="#999"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m4 6 4 4 4-4"
                          />
                        </svg>
                      </div>
                    </button>

                    {isSponsorDropdownOpen && (
                      <ul css={styles.customDropdownOptions}>
                        <li>
                          <button
                            type="button"
                            css={styles.customDropdownOption}
                            onClick={() => {
                              setSelectedSponsorId('')
                              setIsSponsorDropdownOpen(false)
                              if (form?.fields?.sponsors?.onChange) {
                                form.fields.sponsors.onChange({
                                  target: { value: null },
                                })
                              }
                              handleSponsorChange(null)
                            }}
                            onKeyDown={handleKeyboardActivation((event) =>
                              event.currentTarget.click()
                            )}
                          >
                            <Text size={14} css={styles.placeholderText}>
                              Select sponsor
                            </Text>
                          </button>
                        </li>
                        {loadedSponsors.map((sponsor) => (
                          <li key={sponsor.id}>
                            <button
                              type="button"
                              css={styles.customDropdownOption}
                              onClick={() => {
                                setSelectedSponsorId(sponsor.id.toString())
                                setIsSponsorDropdownOpen(false)
                                if (form?.fields?.sponsors?.onChange) {
                                  setTimeout(() => {
                                    form.fields.sponsors.onChange({
                                      target: { value: sponsor },
                                    })
                                  }, 0)
                                }
                                handleSponsorChange(sponsor)
                              }}
                              onKeyDown={handleKeyboardActivation((event) =>
                                event.currentTarget.click()
                              )}
                            >
                              {sponsor.logo && (
                                <InWinImage
                                  source={sponsor.logo}
                                  alt={sponsor.fullName}
                                  css={styles.beneficiaryLogo}
                                />
                              )}
                              <Text size={14} css={styles.beneficiaryName}>
                                {sponsor.fullName}
                              </Text>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {/* Dashed Line Separator - only if no sponsor selected */}
              {!form?.fields?.sponsors?.value && (
                <div css={styles.dashedSeparatorInForm}></div>
              )}

              {/* Sponsor Amount - only show if sponsor is selected */}
              {form?.fields?.sponsors?.value && (
                <>
                  <div css={styles.fieldSection}>
                    <button
                      type="button"
                      css={styles.amountFieldContainer}
                      onClick={() => {
                        const input = document.querySelector(
                          'input[name="sponsorAmount"]'
                        )
                        if (input) {
                          input.focus()
                        }
                      }}
                      aria-label="Focus sponsor amount input"
                      onKeyDown={handleKeyboardActivation((event) =>
                        event.currentTarget.click()
                      )}
                    >
                      {/* Always show Amount placeholder */}
                      <Text size={14} css={styles.amountPlaceholder}>
                        Amount
                      </Text>

                      {/* Show $0 when no value, show actual value when there is one */}
                      <div css={styles.formattedAmountDisplay}>
                        <Text size={14} css={styles.amountValue}>
                          $ {sponsorAmountValue || '0'}
                        </Text>
                      </div>

                      <input
                        name="sponsorAmount"
                        type="number"
                        value={sponsorAmountValue}
                        placeholder=""
                        onChange={(e) => {
                          const newValue = e.target.value
                          setSponsorAmountValue(newValue)

                          if (form?.fields?.sponsorAmount?.onChange) {
                            form.fields.sponsorAmount.onChange({
                              target: { value: newValue },
                            })
                          }
                        }}
                        css={styles.hiddenAmountInput}
                      />
                    </button>
                  </div>

                  {form?.fields?.sponsors?.value && (
                    <div css={styles.sponsorLimitSection}>
                      <Text size={12} css={styles.sponsorLimitText}>
                        Sponsor&apos;s limit is ${' '}
                        {sponsorLimit > 0 ? sponsorLimit : 20}
                      </Text>
                    </div>
                  )}
                </>
              )}

              {/* Dashed Line Separator - only if sponsor selected */}
              {form?.fields?.sponsors?.value && (
                <div css={styles.dashedSeparatorInForm}></div>
              )}

              {/* Enter Amount */}
              <div css={styles.fieldSection}>
                <Text size={12} bold css={styles.fieldLabel}>
                  Enter InWin amount
                </Text>
                <Text size={12} css={styles.fieldSubtext}>
                  The InWin amount is subtracted from your balance
                </Text>
                <button
                  type="button"
                  css={styles.amountFieldContainer}
                  onClick={() => {
                    const input = document.querySelector('input[name="amount"]')
                    if (input) {
                      input.focus()
                    }
                  }}
                  aria-label="Focus InWin amount input"
                  onKeyDown={handleKeyboardActivation((event) =>
                    event.currentTarget.click()
                  )}
                >
                  {/* Always show Amount placeholder */}
                  <Text size={14} css={styles.amountPlaceholder}>
                    Amount
                  </Text>

                  {/* Show $0 when no value, show actual value when there is one */}
                  <div css={styles.formattedAmountDisplay}>
                    <Text size={14} css={styles.amountValue}>
                      $ {mainAmountValue || '0'}
                    </Text>
                  </div>

                  <input
                    name="amount"
                    type="number"
                    value={mainAmountValue}
                    placeholder=""
                    onChange={(e) => {
                      const newValue = e.target.value
                      setMainAmountValue(newValue)

                      if (form?.fields?.amount?.onChange) {
                        form.fields.amount.onChange({
                          target: { value: newValue },
                        })
                      }
                    }}
                    css={styles.hiddenAmountInput}
                  />
                </button>
              </div>

              {/* Dashed Line Separator */}
              <div css={styles.dashedSeparatorInFormSmallTop}></div>

              {/* Total InWin */}
              <div css={styles.totalSection}>
                <div css={styles.totalContainer}>
                  <div css={styles.totalLabelContainer}>
                    <img
                      src="./img/inwin-tiny-white-logo.png"
                      alt="InWin Logo"
                    />
                    <Text size={12} bold css={styles.totalLabel}>
                      Total InWin
                    </Text>
                  </div>
                  <Text size={14} css={styles.totalAmount}>
                    ${totalAmount}
                  </Text>
                </div>
              </div>

              {/* Confirm Button */}
              <div css={styles.buttonSection}>
                <FormButton
                  type="submit"
                  css={styles.confirmButton}
                  disabled={
                    !form?.fields?.beneficiary?.value ||
                    !form?.fields?.triggers?.value ||
                    !mainAmountValue
                  }
                >
                  <Text size={14} bold>
                    Confirm InWin
                  </Text>
                </FormButton>
              </div>
            </Form>
          )}
        </div>

        {/* Confirmation Modal */}
        {showConfirmation && (
          <div css={styles.confirmationOverlay}>
            <div css={styles.confirmationModal}>
              <div css={styles.confirmationHeader}>
                <Text size={16} bold css={styles.confirmationTitle}>
                  Confirmation
                </Text>
              </div>

              <div css={styles.confirmationContent}>
                <div css={styles.confirmationRow}>
                  <Text size={12} css={styles.confirmationLabel}>
                    Trigger
                  </Text>
                  <Text size={12} css={styles.confirmationValue}>
                    {form?.fields?.triggers?.value?.description || ''}
                  </Text>
                </div>

                <div css={styles.confirmationRow}>
                  <Text size={12} css={styles.confirmationLabel}>
                    Date
                  </Text>
                  <Text size={12} css={styles.confirmationValue}>
                    {game?.startTime
                      ? format(
                          parseISO(game.startTime),
                          'MMM dd, yyyy'
                        ).toUpperCase()
                      : ''}
                  </Text>
                </div>

                <div css={styles.confirmationRow}>
                  <Text size={12} css={styles.confirmationLabel}>
                    Time
                  </Text>
                  <Text size={12} css={styles.confirmationValue}>
                    {game?.startTime
                      ? `${format(
                          parseISO(game.startTime),
                          'hh:mm aa'
                        )} (CEST, UTC+2)`
                      : ''}
                  </Text>
                </div>

                <div css={styles.confirmationRow}>
                  <Text size={12} css={styles.confirmationLabel}>
                    Total InWin
                  </Text>
                  <Text size={12} css={styles.confirmationValue}>
                    $ {totalAmount}
                  </Text>
                </div>
              </div>

              <div css={styles.confirmationActions}>
                <button
                  css={styles.confirmationButton}
                  onClick={handleFinalSubmit}
                >
                  <Text size={12} bold css={styles.confirmationButtonText}>
                    Confirm InWin
                  </Text>
                </button>

                <button
                  type="button"
                  css={styles.changeDetailsText}
                  onClick={() => setShowConfirmation(false)}
                  aria-label="Change details"
                  onKeyDown={handleKeyboardActivation((event) =>
                    event.currentTarget.click()
                  )}
                >
                  Change details
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Modal */}
        {showError && (
          <div css={styles.confirmationOverlay}>
            <div css={styles.errorModal}>
              <div css={styles.errorHeader}>
                <Text size={18} bold css={styles.errorTitle}>
                  InWin was not placed.
                </Text>
              </div>

              <div css={styles.errorContent}>
                <Text size={14} css={styles.errorMessage}>
                  {errorMessage}
                </Text>
              </div>

              <div css={styles.errorActions}>
                <button
                  css={styles.errorButton}
                  onClick={() => {
                    setShowError(false)
                    setShowTopError(false)
                  }}
                >
                  <Text size={14} bold css={styles.errorButtonText}>
                    Got it
                  </Text>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccess && (
          <div css={styles.confirmationOverlay}>
            <div css={styles.successModal}>
              <div css={styles.successHeader}>
                <Text size={18} bold css={styles.successTitle}>
                  InWin Placed!
                </Text>
              </div>

              <div css={styles.successContent}>
                <Text size={14} css={styles.successMessage}>
                  You have successfully placed your InWin
                </Text>
              </div>

              <div css={styles.successActions}>
                <button
                  css={styles.successButton}
                  onClick={() => {
                    setShowSuccess(false)
                    setShowTopSuccess(false)
                    onClose() // Close the main modal
                  }}
                >
                  <Text size={14} bold css={styles.successButtonText}>
                    Got it
                  </Text>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Top Error Message */}
        {showTopError && (
          <div css={styles.topErrorMessage}>
            <div css={styles.topErrorContent}>
              <div css={styles.topErrorIcon}>✕</div>
              <Text size={14} css={styles.topErrorText}>
                Changes unsaved
              </Text>
            </div>
          </div>
        )}

        {/* Top Success Message */}
        {showTopSuccess && (
          <div css={styles.topSuccessMessage}>
            <div css={styles.topSuccessContent}>
              <div css={styles.topSuccessIcon}>✓</div>
              <Text size={14} css={styles.topSuccessText}>
                Changes saved!
              </Text>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

MobileInWinModalComponent.propTypes = {
  game: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    startTime: PropTypes.string,
    teamA: PropTypes.string,
    teamB: PropTypes.string,
    teamOne: PropTypes.shape({
      logo: PropTypes.string,
      teamName: PropTypes.string,
    }),
    teamTwo: PropTypes.shape({
      logo: PropTypes.string,
      teamName: PropTypes.string,
    }),
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  form: PropTypes.shape(),
  onSubmit: PropTypes.func.isRequired,
  currentBalance: PropTypes.number,
  loadBeneficiaries: PropTypes.func.isRequired,
  loadTriggers: PropTypes.func.isRequired,
  loadSponsors: PropTypes.func.isRequired,
}

export default MobileInWinModalComponent
